import React, { useState, useEffect, useRef } from 'react';
import { MobileLine } from '../types/MobileLine';
import LinhaCard from './LinhaCard';

interface PaginatedColumnViewProps {
  title: string;
  status: 'inadimplente' | 'adimplente';
  confirmed: boolean | null;
  lines: MobileLine[];
  onVincular: (numero: string) => void;
  onAtualizarLinha: (numero: string, data: Partial<MobileLine>, silencioso?: boolean) => void;
  onExcluir: (numero: string) => void;
  onAtualizarFaturas: (numero: string) => void;
  onRemoverVinculo: (numero: string) => void;
  openModal: (title: string, message: string, onConfirm?: () => void, showConfirmButton?: boolean, showCancelButton?: boolean) => void;
  borderColor: 'red' | 'orange' | 'green';
  itemsPerPage?: number;
}

const PaginatedColumnView: React.FC<PaginatedColumnViewProps> = ({
  title,
  lines,
  onVincular,
  onAtualizarLinha,
  onExcluir,
  onAtualizarFaturas,
  onRemoverVinculo,
  openModal,
  borderColor,
  itemsPerPage = 15,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);

  // Calcular índices de paginação
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLines = lines.slice(startIndex, endIndex);
  const totalPages = Math.ceil(lines.length / itemsPerPage);

  // Reset de página quando linhas mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [lines]);

  // Auto-scroll ao início quando muda de página
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [currentPage]);

  const borderClass = {
    red: 'border-red-700',
    orange: 'border-orange-500',
    green: 'border-green-500',
  }[borderColor];

  const titleColorClass = {
    red: 'text-red-500',
    orange: 'text-orange-400',
    green: 'text-green-400',
  }[borderColor];

  const buttonBaseClass = {
    red: 'hover:bg-red-700 focus:ring-red-500',
    orange: 'hover:bg-orange-700 focus:ring-orange-500',
    green: 'hover:bg-green-700 focus:ring-green-500',
  }[borderColor];

  const buttonBgClass = {
    red: 'bg-red-600',
    orange: 'bg-orange-600',
    green: 'bg-green-600',
  }[borderColor];

  return (
    <div className={`bg-zinc-800 border ${borderClass} rounded-xl p-4 shadow-lg flex flex-col`}>
      {/* Header com título e contador */}
      <h2 className={`${titleColorClass} font-bold mb-4 text-xl flex items-center justify-between`}>
        <span>{title} ({lines.length})</span>
        {totalPages > 1 && (
          <span className="text-sm text-gray-400 font-normal">
            Página {currentPage} de {totalPages}
          </span>
        )}
      </h2>

      {/* Container de linhas com overflow */}
      <div
        ref={containerRef}
        className="space-y-2 flex-1 overflow-y-auto min-h-[500px] max-h-[70vh] pr-2"
      >
        {paginatedLines.length === 0 && lines.length === 0 ? (
          <p className="text-gray-400 text-center py-4">Nenhuma linha neste status</p>
        ) : paginatedLines.length === 0 ? (
          <p className="text-gray-400 text-center py-4">Nenhuma linha nesta página</p>
        ) : (
          paginatedLines.map((line) => (
            <LinhaCard
              key={line.numero}
              line={line}
              onVincular={() => onVincular(line.numero)}
              onAtualizarLinha={onAtualizarLinha}
              onExcluir={onExcluir}
              onAtualizarFaturas={onAtualizarFaturas}
              onRemoverVinculo={() => onRemoverVinculo(line.numero)}
              openModal={openModal}
            />
          ))
        )}
      </div>

      {/* Controles de paginação */}
      {totalPages > 1 && (
        <div className="mt-4 flex gap-2 justify-between items-center border-t border-zinc-700 pt-4">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className={`px-3 py-2 rounded-md text-sm font-medium text-white transition-colors ${
              currentPage === 1
                ? 'bg-gray-600 cursor-not-allowed opacity-50'
                : `${buttonBgClass} ${buttonBaseClass}`
            }`}
          >
            ← Anterior
          </button>

          {/* Indicador de páginas */}
          <div className="flex gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = currentPage > 2 ? currentPage - 2 + i : i + 1;
              if (pageNum > totalPages) return null;

              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-8 h-8 rounded text-xs font-medium transition-colors ${
                    currentPage === pageNum
                      ? `${buttonBgClass} text-white`
                      : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className={`px-3 py-2 rounded-md text-sm font-medium text-white transition-colors ${
              currentPage === totalPages
                ? 'bg-gray-600 cursor-not-allowed opacity-50'
                : `${buttonBgClass} ${buttonBaseClass}`
            }`}
          >
            Próxima →
          </button>
        </div>
      )}
    </div>
  );
};

export default PaginatedColumnView;
